
import { useEffect, useState } from "react";
import { Form, Button, Switch, Select, Checkbox, Input } from "antd";
import { useTranslation } from "react-i18next";
import { LoadingOutlined, StarFilled } from "@ant-design/icons";
import AccountData from "../../../models/AccountData";
import { KeyValue } from "../../../models/CommunicationsData";
import './ValidateAccountForm.scss';
import { Contract } from "../../../models/Contract";
import HTMLReactParser from "html-react-parser";
import { PaymentConfigurationData, PaymentMethodData } from "../../../models/PaymentMethodData";
import ApiResponse from "../../../models/api/ApiResponse";
import { ConfigurationAvailableForAccount, PossibleConfigurationAvailableForAccount } from "../../../models/AccountAsignationData";
import { GetConfigurationAvailableForAccount } from "../../../api/repositoryEP";
import LoadingDataComponent from "../../common/LoadingData/LoadingDataComponent";

interface ValidateAccountFormProps {
    drawerVisible: boolean;
    setDrawerVisible: (visible: boolean) => void;
    accountData: AccountData | undefined;
    commissionConfiguration: PossibleConfigurationAvailableForAccount[] | undefined;
    loadingCommissionConfiguration?: boolean;
    loadingPaymentConfiguration?: boolean;
    paymentsConfiguration: ConfigurationAvailableForAccount[] | undefined;
    onValidateAccount: (id: string, isActive: boolean, typeAccount: string, contractConfigurationId: string[], paymentConfigurationId: string, useSpecificConfiguration?: boolean, verificationCode?: string) => Promise<ApiResponse | null>;
}


const ValidateAccountForm = (props: ValidateAccountFormProps): JSX.Element => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [loadingButton, setLoadingButton] = useState<boolean>(false);

    const closeDrawer = () => {
        form.resetFields();
        props.setDrawerVisible(false)
    }


    const validateChecked = Form.useWatch('validate', form);
    const cc = Form.useWatch('commissionConfiguration', form);
    const paymentMethod = Form.useWatch('paymentMethod', form);
    const useSpecificConfiguration = Form.useWatch('useSpecificConfiguration', form);
    const mandatoryConfigurations = props.commissionConfiguration?.filter(c => c.isMandatory) ?? [];
    const selectableConfigurations = props.commissionConfiguration?.filter(c => !c.isMandatory) ?? [];

    const isFormValid =
        validateChecked === true &&
        (Array.isArray(cc) && cc.length > 0 || (!useSpecificConfiguration && mandatoryConfigurations.length > 0)) &&
        !!paymentMethod;

    const fiscalIdSnippet = props.accountData?.fiscalId
        ? t("components.accounts.list.validate-account-fiscal-id-snippet", { 0: props.accountData.fiscalId })
        : "";

    const descriptionKey = props.accountData?.verificationCode
        ? "components.accounts.list.validate-account-description-with-verification-code"
        : "components.accounts.list.validate-account-description";



    const onFinish = async (values: any) => {
        if (props.accountData?.id) {
            try {
                setLoadingButton(true)

                // combinamos las seleccionadas + las mandatory (si useSpecificConfiguration no está marcado)
                const selectedIds: string[] = Array.isArray(values.commissionConfiguration) ? values.commissionConfiguration : [];
                const mandatoryIds = values.useSpecificConfiguration ? [] : mandatoryConfigurations.map(c => c.id);
                const allIds = Array.from(new Set([...selectedIds, ...mandatoryIds]));
                const commissionConfigurationIds: string[] = allIds.filter(id => {
                    const config = props.commissionConfiguration?.find(c => c.id === id);
                    return !(config?.isDefault === true && config?.hasToAddAccountToConfiguration === false);
                });



                const selectedPaymentConfiguration = props.paymentsConfiguration
                    ?.find(c => c.id === values.paymentMethod);
                const paymentConfigurationId =
                    selectedPaymentConfiguration?.isDefault === true &&
                        selectedPaymentConfiguration?.hasToAddAccountToConfiguration === false
                        ? ""
                        : values.paymentMethod;

                const res = await props.onValidateAccount(
                    props.accountData.id,
                    true,
                    props.accountData?.type,
                    commissionConfigurationIds,
                    paymentConfigurationId,
                    !!values.useSpecificConfiguration,
                    values.verificationCode
                );
                if (res && res.type === 1 && res.data == true) {
                    setLoadingButton(false)
                    closeDrawer();
                }
            } finally {
                setLoadingButton(false)
            }
        }
        return;
    };

    useEffect(() => {
        if (!props.drawerVisible) {
            form.resetFields();
            setLoadingButton(false);
            return;
        }

        const defaultPaymentMethodId = props.paymentsConfiguration && props.paymentsConfiguration.length > 0
            ? (props.paymentsConfiguration.find(c => c.isDefault)?.id ?? props.paymentsConfiguration[0]?.id)
            : undefined;

        form.setFieldsValue({
            validate: false,
            useSpecificConfiguration: false,
            commissionConfiguration: undefined,
            paymentMethod: defaultPaymentMethodId,
            verificationCode: props.accountData?.verificationCode ?? undefined
        });
    }, [props.drawerVisible, props.accountData?.id, props.accountData?.verificationCode, props.paymentsConfiguration]);


    if (props.loadingCommissionConfiguration || props.loadingPaymentConfiguration) {
        return <LoadingDataComponent />;
    }


    return (
        <div className='validate-account-form-component'>

            <Form form={form} onFinish={onFinish} layout="vertical">

                {/* ── Descripción + checkbox de validación ────────────── */}
                <div className="info-container">
                    {HTMLReactParser(t(descriptionKey, {
                        0: props.accountData?.commercialName,
                        1: fiscalIdSnippet
                    }))}
                </div>

                {props.accountData?.verificationCode && (
                    <div className="verification-code-container">
                        <Form.Item
                            name="verificationCode"
                            label={t("components.accounts.list.validate-account-verification-code-label")}
                        >
                            <Input className="app-input" />
                        </Form.Item>
                    </div>
                )}

                <div className="checkbox-container">
                    <Form.Item name="validate" valuePropName="checked" rules={[{ required: true }]} initialValue={false}>
                        <Checkbox className="checkbox-title">{t("components.accounts.list.validate-account-checkbox") || ""}</Checkbox>
                    </Form.Item>
                </div>

                {/* ── Bloque: Configuración de contrato ────────────────── */}
                <div className="form-section">
                    <div className="form-section-header">
                        <span className="form-section-title">{t("components.accounts.list.validate-account-contract-title")}</span>
                        <span className="form-section-desc">{t("components.accounts.list.validate-account-contract-description") || "Selecciona qué configuración de contratos se aplicará a esta cuenta."}</span>
                    </div>
                    <Form.Item
                        name="commissionConfiguration"
                        required={useSpecificConfiguration || mandatoryConfigurations.length === 0}
                        className="contract-selector"
                        rules={useSpecificConfiguration || mandatoryConfigurations.length === 0
                            ? [{ required: true, message: t("components.accounts.list.validate-account-select-at-least-one-configuration") as string }]
                            : []}
                    >
                        <Select className="app-button" mode="multiple" allowClear optionLabelProp="label">
                            {[...selectableConfigurations].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)).map((cc, index) => (
                                <Select.Option key={index} value={cc.id} label={cc.name}>
                                    {cc.name}
                                    {cc.isDefault && <StarFilled style={{ color: '#faad14', marginLeft: 8 }} />}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="useSpecificConfiguration" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                        <Checkbox className="checkbox-title">
                            {t("components.accounts.list.validate-account-checkbox-use-specific-configuration") || ""}
                        </Checkbox>
                    </Form.Item>

                    {mandatoryConfigurations.length > 0 && !useSpecificConfiguration && (
                        <div className="mandatory-configs">
                            <span className="mandatory-label">{t("components.accounts.list.validate-account-mandatory-configurations")}</span>
                            <ul className="mandatory-list">
                                {mandatoryConfigurations.map((mc) => (
                                    <li key={mc.id} className="mandatory-item">
                                        {mc.name}
                                        {mc.isDefault && <StarFilled style={{ color: '#faad14', marginLeft: 6 }} />}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* ── Bloque: Método de pago ─────────────────────────── */}
                <div className="form-section">
                    <div className="form-section-header">
                        <span className="form-section-title">{t("components.accounts.list.validate-account-payment-method-title")}</span>
                        <span className="form-section-desc">{t("components.accounts.list.validate-account-payment-description") || "Selecciona el método de pago por defecto para esta cuenta."}</span>
                    </div>
                    <Form.Item name="paymentMethod" className="contract-selector" rules={[{ required: true }]}>
                        <Select
                            className="app-button"
                            optionLabelProp="label"
                            options={[...(props.paymentsConfiguration ?? [])]
                                .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                                .map((pm) => ({ value: pm.id, label: pm.name }))}
                            optionRender={(option) => (
                                <span>
                                    {option.label}
                                    {props.paymentsConfiguration?.find(c => c.id === option.value)?.isDefault && (
                                        <StarFilled style={{ color: '#faad14', marginLeft: 8 }} />
                                    )}
                                </span>
                            )}
                        />
                    </Form.Item>
                </div>

                {/* ── Pie ─────────────────────────────────────────────── */}
                <div className="all-required-container">{t("all-required")}</div>
                <div className="buttons-container">
                    <Button className="app-button reverse" onClick={closeDrawer}>
                        {t("button.cancel")}
                    </Button>
                    <Button className="app-button accept-button" htmlType="submit" disabled={!isFormValid || loadingButton} loading={loadingButton}>
                        {t("button.accept")}
                    </Button>
                </div>

            </Form>
        </div>
    );
};

export default ValidateAccountForm